import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Download,
  Edit,
  Delete,
  CalendarMonth,
  People,
  Description,
  Close,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../components/layout/Cslayout";
import axios from "axios";
import config from "../config.js";

const BoardMeetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [stats, setStats] = useState({
    totalMeetings: 0,
    totalAttendees: 0,
    totalDocuments: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Get user ID from Redux store
  const { user } = useSelector((state) => state.auth);
  const userId = user?.id || user?._id;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      date: "",
      time: "",
      status: "Draft",
      attendees: 0,
      documents: 0,
    },
  });

  const { API_BASE_URL } = config;

  useEffect(() => {
    fetchMeetings();
  }, [userId]);

  const fetchMeetings = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/getMeetings`, {
        headers: { Authorization: `Bearer ${userId}` },
      });

      const meetingsData = response.data?.data || [];
      setMeetings(meetingsData);

      const stats = {
        totalMeetings: meetingsData.length,
        totalAttendees: meetingsData.reduce(
          (sum, meeting) => sum + (meeting.attendees || 0),
          0
        ),
        totalDocuments: meetingsData.reduce(
          (sum, meeting) => sum + (meeting.documents || 0),
          0
        ),
      };
      setStats(stats);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch meetings");
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (!userId) {
      setError("User authentication required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...data,
        userId: userId,
      };

      if (editingMeeting) {
        await axios.put(
          `${API_BASE_URL}/api/v1/updateMeeting/${editingMeeting._id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${userId}` },
          }
        );
        setSuccess("Meeting updated successfully");
      } else {
        await axios.post(`${API_BASE_URL}/api/v1/createMeeting`, payload, {
          headers: { Authorization: `Bearer ${userId}` },
        });
        setSuccess("Meeting created successfully");
      }
      handleCloseDialog();
      fetchMeetings();
    } catch (err) {
      setError(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (meetingId) => {
    if (!window.confirm("Are you sure you want to delete this meeting?"))
      return;

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/deleteMeeting/${meetingId}`, {
        headers: { Authorization: `Bearer ${userId}` },
      });
      setSuccess("Meeting deleted successfully");
      fetchMeetings();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete meeting");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (meeting) => {
    setEditingMeeting(meeting);
    Object.keys(meeting).forEach((key) => {
      if (key in control._defaultValues) {
        setValue(key, meeting[key]);
      }
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMeeting(null);
    reset();
  };

  const StatsCard = ({ title, value, icon }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {icon}
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5">{value}</Typography>
          </Box>
        </Box>
      </Card>
    </motion.div>
  );

  const getStatusColor = (status) =>
    ({
      Completed: "success",
      Upcoming: "warning",
      Draft: "default",
    }[status] || "default");

  if (!userId) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Please log in to access board meetings.
          </Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <AnimatePresence>
          {(error || success) && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {error && (
                <Alert
                  severity="error"
                  onClose={() => setError("")}
                  sx={{ mb: 2 }}
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert
                  severity="success"
                  onClose={() => setSuccess("")}
                  sx={{ mb: 2 }}
                >
                  {success}
                </Alert>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
            <Typography variant="h4">Board Meetings</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ borderRadius: 2 }}
              onClick={() => setOpenDialog(true)}
              disabled={loading}
            >
              New Meeting
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <StatsCard
                title="Total Meetings"
                value={stats.totalMeetings}
                icon={<CalendarMonth color="primary" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatsCard
                title="Total Attendees"
                value={stats.totalAttendees}
                icon={<People color="secondary" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatsCard
                title="Total Documents"
                value={stats.totalDocuments}
                icon={<Description color="error" />}
              />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Meeting Title</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Attendees</TableCell>
                    <TableCell>Documents</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(meetings) && meetings.length > 0 ? (
                    meetings.map((meeting) => (
                      <TableRow key={meeting._id}>
                        <TableCell>{meeting.title}</TableCell>
                        <TableCell>
                          {new Date(meeting.date).toLocaleDateString()}
                          <Typography variant="body2" color="text.secondary">
                            {meeting.time}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={meeting.status}
                            color={getStatusColor(meeting.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{meeting.attendees}</TableCell>
                        <TableCell>{meeting.documents}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(meeting)}
                            disabled={loading}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(meeting._id)}
                            disabled={loading}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No meetings found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </motion.div>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          {...(loading && {
            disableEscapeKeyDown: true,
            disableBackdropClick: true,
          })}
        >
          <DialogTitle>
            {editingMeeting ? "Edit Meeting" : "New Meeting"}
            <IconButton
              onClick={handleCloseDialog}
              sx={{ position: "absolute", right: 8, top: 8 }}
              disabled={loading}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              <Stack spacing={3}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: "Title is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Meeting Title"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
                <Controller
                  name="date"
                  control={control}
                  rules={{ required: "Date is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="date"
                      label="Date"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                    />
                  )}
                />
                <Controller
                  name="time"
                  control={control}
                  rules={{ required: "Time is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="time"
                      label="Time"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                    />
                  )}
                />
                <Controller
                  name="status"
                  control={control}
                  rules={{ required: "Status is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Status"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                      disabled={loading}
                    >
                      <MenuItem value="Draft">Draft</MenuItem>
                      <MenuItem value="Upcoming">Upcoming</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                    </TextField>
                  )}
                />
                <Controller
                  name="attendees"
                  control={control}
                  rules={{
                    required: "Number of attendees is required",
                    min: { value: 1, message: "Must have at least 1 attendee" },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Number of Attendees"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
                <Controller
                  name="documents"
                  control={control}
                  rules={{
                    required: "Number of documents is required",
                    min: { value: 0, message: "Cannot be negative" },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Number of Documents"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                      disabled={loading}
                    />
                  )}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {editingMeeting ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default BoardMeetings;
